import { AddModalProps } from '../../types';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  HStack,
  Input,
  Spacer,
  VStack,
} from '@chakra-ui/react';
import { Labeled } from '@partiu/ui/components/molecules/Labeled.tsx';
import { useForm } from 'react-hook-form';
import slugify from 'slugify';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import {
  useAgreementPermissions,
  useCreateAgreementStaffMutation,
  useUpdateAgreementStaffMutation,
} from '@partiu/api/dashboard/hooks.ts';
import { useEffect, useMemo, useState } from 'react';
import { IAgreementStaff } from '@partiu/api/dashboard/types.ts';

const translations: Record<string, string> = {
  banner: 'Pode ver/editar Banners',
  settings: 'Pode editar configurações',
  vinculation: 'Pode vincular CPF',
  utilization: 'Pode ver utilizações',
  'partner-stores': 'Pode ver/criar Lojas Parceiras',
  push_notification: 'Pode ver/criar Emails e Notificações',
  userbase: 'Pode ver base de usuários',
  magazines: 'Pode acessar módulo de revistas',
  referral: 'Pode acessar módulo de indicações',
  promocode: 'Pode ver/editar base de códigos promocionais',
  map_plans: 'Pode acessar módulo Super App',
};

function getEmailUsername(fullName: string): string {
  const cleanedName = fullName.split('@')[0];
  const split = cleanedName.split(' ');
  if (cleanedName.length < 1 || split.length === 1) return cleanedName;

  return split[0][0] + split[split.length - 1];
}

export function StaffForm({ onClose, instance, options }: AddModalProps<IAgreementStaff>) {
  const { name, id } = useTheme();
  const { register, handleSubmit, watch } = useForm({
    defaultValues: {
      fullName: instance?.fullName || '',
      password: '',
    },
  });
  const fullName = watch('fullName');
  const generatedEmail = useMemo(
    () => slugify(getEmailUsername(fullName), '_').toLowerCase() + '@' + slugify(name, '').toLowerCase() + '.com',
    [fullName, name]
  );
  // null = segue o nome; ao digitar, o usuário assume o controle do email.
  const [customEmail, setCustomEmail] = useState<string | null>(instance ? instance.email : null);
  const email = customEmail ?? generatedEmail;
  const [permissions, setPermissions] = useState<string[]>([]);
  const { data: agreementPermissions = [] } = useAgreementPermissions();
  const createStaffMutation = useCreateAgreementStaffMutation(options);
  const updateStaffMutation = useUpdateAgreementStaffMutation(options);
  const mutation = instance ? updateStaffMutation : createStaffMutation;

  const allPermissions = useMemo(
    () => Array.from(new Set(agreementPermissions)),
    [agreementPermissions]
  );

  useEffect(() => {
    if (!instance) return;
    setPermissions(instance.agreementStaffPermissions);
  }, [instance]);

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit((data) =>
        mutation.mutate({
          agreementStaffPermissions: permissions,
          email,
          fullName,
          password: data.password,
          agreementID: id as number,
          id: instance?.id,
        })
      )}
    >
      <VStack spacing={5}>
        <Labeled label={'Nome Completo'}>
          <Input
            disabled={!!instance}
            type={'text'}
            autoComplete="one-time-code"
            {...register('fullName', { required: true, minLength: 5 })}
          />
        </Labeled>
        <Labeled label={'Email'} helpText={'Sugerido a partir do nome, pode ser alterado'}>
          <Input
            type={'email'}
            value={email}
            onChange={(e) => setCustomEmail(e.target.value.trim().toLowerCase())}
            placeholder="usuario@empresa.com"
          />
        </Labeled>
        <Labeled
          label={'Senha'}
          helpText={instance ? 'Deixe em branco para não trocar a senha' : ''}
          footerText={'Pelo menos 8 caracteres'}
        >
          <Input
            type={'password'}
            autoComplete={'false'}
            {...register('password', { required: !instance, minLength: 8 })}
          />
        </Labeled>
        <Labeled label={'Permissões'} helpText={'Escolha pelo menos uma permissão'}>
          <CheckboxGroup value={permissions} onChange={(e) => setPermissions(e as string[])} colorScheme="green">
            <VStack width={'100%'} align={'flex-start'} spacing={3} paddingTop={'5px'}>
              {allPermissions.map((p) => {
                const label = translations[p] ?? p;
                return (
                  <Checkbox margin={0} key={p} value={p}>
                    {label}
                  </Checkbox>
                );
              })}
            </VStack>
          </CheckboxGroup>
        </Labeled>
      </VStack>
      <HStack paddingTop={'20px'} width={'100%'}>
        <Spacer />
        <Button isLoading={mutation.isLoading} variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          isLoading={mutation.isLoading}
          variant={'solid'}
          isDisabled={permissions.length < 1 || !email}
          colorScheme={'green'}
          type={'submit'}
        >
          {instance ? 'Salvar' : 'Criar'}
        </Button>
      </HStack>
    </form>
  );
}
